import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Grid,
  Button,
  Card,
  CardContent,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SpeedIcon from '@mui/icons-material/Speed';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import BuildIcon from '@mui/icons-material/Build';
import SecurityIcon from '@mui/icons-material/Security';
import DevicesIcon from '@mui/icons-material/Devices';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import WarriliLogo from '../assets/logo-white.png'; // Make sure this path is correct

// Main container
const MissionContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#000000',
  color: '#ffffff',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
}));

// Logo styling
const LogoImage = styled('img')({
  height: '40px',
  marginRight: '10px',
});

// Mission Statement Section
const BlackSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#000000',
  padding: '80px 0',
  [theme.breakpoints.down('md')]: {
    padding: '60px 0',
  },
}));

const MissionStatement = styled(Box)(({ theme }) => ({
  marginBottom: '60px',
  textAlign: 'center',
  maxWidth: '900px',
  margin: '0 auto 60px auto',
}));

const MissionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: '30px',
  fontWeight: 700,
  fontSize: '2.5rem',
  position: 'relative',
  display: 'inline-block',
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: '-10px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '80px',
    height: '4px',
    backgroundColor: '#FFD700',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
  },
}));

const MissionDescription = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  lineHeight: 1.7,
  marginBottom: '20px',
  color: '#e0e0e0',
}));

const HighlightText = styled('span')({
  color: '#FFD700',
  fontWeight: 600,
});

// Stats Cards
const StatCard = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  borderRadius: '10px',
  padding: '30px 20px',
  textAlign: 'center',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.2)',
  },
}));

const StatNumber = styled(Typography)(({ theme }) => ({
  color: '#FFD700',
  fontWeight: 700,
  marginBottom: '10px',
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  color: '#ffffff',
  fontWeight: 500,
}));

// Features Section
const YellowSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#FFD700',
  padding: '80px 0',
  color: '#000000',
  [theme.breakpoints.down('md')]: {
    padding: '60px 0',
  },
}));

const YellowSectionContent = styled(Container)(({ theme }) => ({
  maxWidth: '1200px',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: '50px',
  fontWeight: 700,
  textAlign: 'center',
  position: 'relative',
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: '-15px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '80px',
    height: '4px',
    backgroundColor: '#000000',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
  },
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  backgroundColor: '#ffffff',
  borderRadius: '10px',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0 15px 30px rgba(0, 0, 0, 0.1)',
  },
}));

const FeatureIconContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#000000',
  color: '#FFD700',
  width: '70px',
  height: '70px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 20px auto',
  '& svg': {
    fontSize: '2rem',
  },
}));

const FeatureTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: '15px',
  textAlign: 'center',
}));

const FeatureDescription = styled(Typography)(({ theme }) => ({
  color: '#555555',
  textAlign: 'center',
}));

// CTA Section
const CTASection = styled(Box)(({ theme }) => ({
  backgroundColor: '#000000',
  padding: '80px 0',
  textAlign: 'center',
  [theme.breakpoints.down('md')]: {
    padding: '60px 0',
  },
}));

const CTATitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: '30px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.8rem',
  },
}));

const CTAButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#FFD700',
  color: '#000000',
  padding: '12px 30px',
  fontSize: '1rem',
  fontWeight: 600,
  borderRadius: '50px',
  textTransform: 'uppercase',
  '&:hover': {
    backgroundColor: '#e6c200',
  },
}));

const Mission1 = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Stats data
  const stats = [
    { number: '24/7', label: 'AI Support Available' },
    { number: '95%', label: 'Issue Resolution Rate' },
    { number: '<2 min', label: 'Average Response Time' },
    { number: '10K+', label: 'Users Helped Daily' },
  ];

  // Features data
  const features = [
    {
      icon: <SpeedIcon />,
      title: 'Instant Solutions',
      description: 'Get immediate answers to technical problems without waiting for human support.',
    },
    {
      icon: <SmartToyIcon />,
      title: 'AI-Powered Diagnostics',
      description: 'Our advanced AI analyzes your issue and provides precise troubleshooting steps.',
    },
    {
      icon: <SupportAgentIcon />,
      title: 'Expert Backup',
      description: 'Complex issues are seamlessly escalated to human experts when needed.',
    },
    {
      icon: <BuildIcon />,
      title: 'Guided Repairs',
      description: 'Step-by-step instructions for hardware and software fixes you can do yourself.',
    },
    {
      icon: <SecurityIcon />,
      title: 'Secure Assistance',
      description: 'All troubleshooting happens in a secure environment protecting your privacy.',
    },
    {
      icon: <DevicesIcon />,
      title: 'Multi-Device Support',
      description: 'Solutions for all your devices - computers, phones, smart home, and more.',
    },
  ];

  return (
    <MissionContainer>
      {/* Logo Display */}
      <Box sx={{ display: 'flex', alignItems: 'center', padding: '20px', backgroundColor: '#000' }}>
        <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
          <LogoImage src={WarriliLogo} alt="Warrili Logo" />
        </Link>
      </Box>
      
      {/* Mission Statement Section */}
      <BlackSection>
        <Container maxWidth="lg">
          <MissionStatement>
            <MissionTitle variant="h2">
              Our Mission
            </MissionTitle>
            <MissionDescription variant="body1">
              At Warrili, our mission is to revolutionize technical support by leveraging the power of artificial intelligence to provide <HighlightText>instant, accurate solutions</HighlightText> to complex technical problems. We believe that technology should work for people, not against them, and when issues arise, resolution should be immediate and stress-free.
            </MissionDescription>
            <MissionDescription variant="body1">
              We are committed to developing AI systems that understand, diagnose, and solve technical issues with unprecedented speed and precision, making expert-level technical support accessible to everyone, regardless of their technical expertise or location.
            </MissionDescription>
            <MissionDescription variant="body1">
              Our goal is to <HighlightText>minimize downtime</HighlightText>, eliminate frustration, and empower users to overcome technical challenges efficiently, allowing them to focus on what truly matters - using technology to enhance their lives and achieve their goals.
            </MissionDescription>
          </MissionStatement>
          
          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <StatCard>
                  <StatNumber variant="h3">
                    {stat.number}
                  </StatNumber>
                  <StatLabel variant="body1">
                    {stat.label}
                  </StatLabel>
                </StatCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </BlackSection>
      
      {/* Features Section */}
      <YellowSection>
        <YellowSectionContent>
          <SectionTitle variant="h2">
            How We Help You
          </SectionTitle>
          
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <FeatureCard elevation={3}>
                  <CardContent sx={{ padding: '30px 20px' }}>
                    <FeatureIconContainer>
                      {feature.icon}
                    </FeatureIconContainer>
                    <FeatureTitle variant="h5">
                      {feature.title}
                    </FeatureTitle>
                    <FeatureDescription variant="body1">
                      {feature.description}
                    </FeatureDescription>
                  </CardContent>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </YellowSectionContent>
      </YellowSection>
      
      {/* CTA Section */}
      <CTASection>
        <Container maxWidth="md">
          <CTATitle variant="h3">
            Ready to experience hassle-free technical support?
          </CTATitle>
          <CTAButton 
            variant="contained" 
            size="large"
            component={Link}
            to="/signup"
            startIcon={<RocketLaunchIcon />}
          >
            Get Started Now
          </CTAButton>
        </Container>
      </CTASection>
    </MissionContainer>
  );
};

export default Mission1;